/** @jsx onetext.jsx.element */

import type { OneText } from '../../../../global';
import type { MagicCableHeroImageProps, Variants } from '../standard';

// eslint-disable-next-line no-restricted-syntax
import {
    EarbudsCaptchaGridImageEight, EarbudsCaptchaGridImageFive, EarbudsCaptchaGridImageFour, EarbudsCaptchaGridImageNine,
    EarbudsCaptchaGridImageOne, EarbudsCaptchaGridImageSeven, EarbudsCaptchaGridImageSix, EarbudsCaptchaGridImageThree,
    EarbudsCaptchaGridImageTwo, MagicCableCaptchaGridImageEight, MagicCableCaptchaGridImageFive,
    MagicCableCaptchaGridImageFour, MagicCableCaptchaGridImageNine, MagicCableCaptchaGridImageOne,
    MagicCableCaptchaGridImageSeven, MagicCableCaptchaGridImageSix, MagicCableCaptchaGridImageThree,
    MagicCableCaptchaGridImageTwo, RayconBlueMagicCableGraphic, RayconMintMagicCableGraphic,
    RayconPurpleMagicCableGraphic
} from '../../images';

declare let onetext : OneText;

const { clsx } = onetext.jsx;

const MagicCableHeroImage = ({ shouldShowAnimation } : MagicCableHeroImageProps) : JSX.Element => {

    return (
        <div className={
            clsx(
                'flex',
                'items-center',
                'justify-center',
                'h-full',
                'w-full',
                'relative',
                'px-5',
                'md:px-0',
                'md:overflow-visible',
                'p-2',
                'md:p-0',
                'md:-mr-24'
            )
        }>
            <div className={
                clsx(
                    'flex flex-col',
                    'items-center',
                    'justify-center',
                    'md:h-auto',
                    'md:w-auto',
                    'h-auto',
                    'w-full',
                    'relative',
                    'overflow-visible',
                    'p-2',
                    'md:p-0',
                    'md:-mr-24 relative'
                )
            }>
                <img
                    src={ RayconMintMagicCableGraphic }
                    alt={ 'Mint Raycon Magic Cable' }
                    style={
                        {
                            animationDelay: '0.3s',
                            willChange:     'opacity, transform'
                        }
                    }
                    className={
                        clsx(
                            'md:h-auto',
                            'h-auto',
                            'max-h-[140px] md:max-h-[240px]',
                            'w-auto',
                            'max-w-full',
                            'md:scale-115',
                            'object-contain',
                            'object-center',
                            'duration-300',
                            'absolute md:right-24 -top-28',
                            'masked-element-bottom-half-gradient',
                            shouldShowAnimation
                                ? clsx(
                                    'transition-transform',
                                    'fade-in-up-raycon-cable animation-fill-mode-both'
                                )
                                : undefined
                        )
                    }
                />
                <img
                    src={ RayconBlueMagicCableGraphic }
                    alt={ 'Blue Raycon Magic Cable' }
                    className={
                        clsx(
                            'md:h-auto',
                            'h-auto',
                            'max-h-[140px] md:max-h-[240px]',
                            'w-auto',
                            'max-w-full',
                            'object-contain',
                            'object-center',
                            'duration-300 z-10',
                            'masked-element-bottom-half-gradient',
                            shouldShowAnimation
                                ? clsx(
                                    'transition-transform',
                                    'fade-in-up-raycon-cable'
                                )
                                : undefined
                        )
                    }
                    style={
                        {
                            animationDelay: '0.5s',
                            willChange:     'opacity, transform'
                        }
                    }
                />
                <img
                    src={ RayconPurpleMagicCableGraphic }
                    alt={ 'Purple Raycon Magic Cable' }
                    className={
                        clsx(
                            'md:h-auto',
                            'h-auto',
                            'max-h-[140px] md:max-h-[240px]',
                            'w-auto',
                            'max-w-full',
                            'object-contain',
                            'object-center',
                            'duration-300',
                            'absolute md:right-24 top-28 z-10',
                            'masked-element-bottom-half-gradient',
                            shouldShowAnimation
                                ? clsx(
                                    'transition-transform',
                                    'fade-in-up-raycon-cable'
                                )
                                : undefined
                        )
                    }
                    style={
                        {
                            animationDelay: '0.7s',
                            willChange:     'opacity, transform'
                        }
                    }
                />

            </div>
        </div>
    );
};

export const weekTwoVariants : Variants = {
    // Magic Spin Cables variants (AppLovin)
    SPIN_CABLE_DISCOUNT_V2: {
        offerTextLine1:          'Up to 60% off',
        offerTextLine2:          'Magic Spin Cables',
        emailPromptText:         'Enter your email to claim your special offer',
        smsPromptText:           'with texts to claim up to 60% off Magic Spin Cables',
        teaserText:              'UP TO 60% OFF',
        showQuiz:                false,
        landingPageUrlPathnames: [
            '/pages/magic-spin-cable-purple',
            '/pages/magic-spin-cable-blue',
            '/pages/magic-spin-cable-mint',
            '/pages/magic-spin-cable-adr5'
        ],
        heroImage:      MagicCableHeroImage,
        propertyValues: {
            'OT - Campaign':     'APPLOVINSPIN',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'SPIN_DISCOUNT',
            'OT - Landing Page': 'DEFAULT'
        },
        externalID: 'spin_discount_v2'
    },
    SPIN_CABLE_CLAIM_OFFER: {
        offerTextLine1:  'Up to 60% off',
        offerTextLine2:  'Magic Spin Cables',
        emailPromptText: 'Enter your email to claim your special offer',
        smsPromptText:   'with texts to claim up to 60% off Magic Spin Cables',
        teaserText:      'UP TO 60% OFF',
        showQuiz:        false,
        claimOffer:      {
            offerTextLine1: 'Up to 60% off',
            offerTextLine2: 'Magic Spin Cables',
            buttonCtaText:  'UP TO 60% OFF'
        },
        heroImage:               MagicCableHeroImage,
        landingPageUrlPathnames: [
            '/pages/magic-spin-cable-purple',
            '/pages/magic-spin-cable-blue',
            '/pages/magic-spin-cable-mint',
            '/pages/magic-spin-cable-adr5'
        ],
        propertyValues: {
            'OT - Campaign':     'APPLOVINSPIN',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'SPIN_DISCOUNT_CLAIM',
            'OT - Landing Page': 'DEFAULT'
        },
        externalID: 'spin_cable_claim_offer'
    },
    SPIN_CABLE_CAPTCHA: {
        offerTextLine1:  'Up to 60% off',
        offerTextLine2:  'Magic Spin Cables',
        emailPromptText: 'Enter your email to claim your special offer',
        smsPromptText:   'with texts to claim up to 60% off Magic Spin Cables',
        teaserText:      'UP TO 60% OFF',
        showQuiz:        false,
        captcha:         {
            titleText: 'Tap all images with the Magic Spin Cable to claim your deal!',
            images:    [
                {
                    url: MagicCableCaptchaGridImageOne
                },
                {
                    url: MagicCableCaptchaGridImageTwo
                },
                {
                    url: MagicCableCaptchaGridImageThree
                },
                {
                    url: MagicCableCaptchaGridImageFour
                },
                {
                    url: MagicCableCaptchaGridImageFive
                },
                {
                    url: MagicCableCaptchaGridImageSix
                },
                {
                    url: MagicCableCaptchaGridImageSeven
                },
                {
                    url: MagicCableCaptchaGridImageEight
                },
                {
                    url: MagicCableCaptchaGridImageNine
                }
            ].reverse()
        },
        heroImage:               MagicCableHeroImage,
        landingPageUrlPathnames: [
            '/pages/magic-spin-cable-purple',
            '/pages/magic-spin-cable-blue',
            '/pages/magic-spin-cable-mint',
            '/pages/magic-spin-cable-adr5'
        ],
        propertyValues: {
            'OT - Campaign':     'APPLOVINSPIN',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'SPIN_DISCOUNT_CAPTCHA',
            'OT - Landing Page': 'DEFAULT'
        },
        externalID: 'spin_cable_captcha'
    },
    // Open Earbuds variants (Meta)
    OPEN_EARBUDS_DISCOUNT_V2: {
        offerTextLine1:          '30% off',
        offerTextLine2:          'Open Earbuds',
        emailPromptText:         'Enter your email to claim your special offer',
        smsPromptText:           'with texts to claim 30% off Open Earbuds',
        teaserText:              'GET 30% OFF',
        showQuiz:                false,
        landingPageUrlPathnames: [ '/pages/raycon-open-earbuds-adr4' ],
        heroImage:               {
            url: 'https://static.onetext.com/misc/open-earbuds-1200x1200.png',
            alt: 'Open Earbuds'
        },
        propertyValues: {
            'OT - Campaign':     'METAO15',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'OPEN_DISCOUNT',
            'OT - Landing Page': 'DEFAULT'
        },
        externalID: 'open_discount_v2'
    },
    OPEN_EARBUDS_CLAIM_OFFER: {
        offerTextLine1:  '30% off',
        offerTextLine2:  'Open Earbuds',
        emailPromptText: 'Enter your email to claim your special offer',
        smsPromptText:   'with texts to claim 30% off Open Earbuds',
        teaserText:      'GET 30% OFF',
        showQuiz:        false,
        claimOffer:      {
            offerTextLine1: '30% off',
            offerTextLine2: 'Open Earbuds',
            buttonCtaText:  'Get 30% OFF'
        },
        landingPageUrlPathnames: [ '/pages/raycon-open-earbuds-adr4' ],
        heroImage:               {
            url: 'https://static.onetext.com/misc/open-earbuds-1200x1200.png',
            alt: 'Open Earbuds'
        },
        propertyValues: {
            'OT - Campaign':     'METAO15',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'OPEN_DISCOUNT_CLAIM',
            'OT - Landing Page': 'DEFAULT'
        },
        externalID: 'open_claim_offer'
    },
    OPEN_EARBUDS_CAPTCHA: {
        offerTextLine1:  '30% off',
        offerTextLine2:  'Open Earbuds',
        emailPromptText: 'Enter your email to claim your special offer',
        smsPromptText:   'with texts to claim 30% off Open Earbuds',
        teaserText:      'GET 30% OFF',
        showQuiz:        false,
        captcha:         {
            titleText: 'Tap all images with Open Earbuds to claim your deal!',
            images:    [
                {
                    url: EarbudsCaptchaGridImageOne
                },
                {
                    url: EarbudsCaptchaGridImageTwo
                },
                {
                    url: EarbudsCaptchaGridImageThree
                },
                {
                    url: EarbudsCaptchaGridImageFour
                },
                {
                    url: EarbudsCaptchaGridImageFive
                },
                {
                    url: EarbudsCaptchaGridImageSix
                },
                {
                    url: EarbudsCaptchaGridImageSeven
                },
                {
                    url: EarbudsCaptchaGridImageEight
                },
                {
                    url: EarbudsCaptchaGridImageNine
                }
            ].reverse()
        },
        landingPageUrlPathnames: [ '/pages/raycon-open-earbuds-adr4' ],
        heroImage:               {
            url: 'https://static.onetext.com/misc/open-earbuds-1200x1200.png',
            alt: 'Open Earbuds'
        },
        propertyValues: {
            'OT - Campaign':     'METAO15',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'OPEN_DISCOUNT_CAPTCHA',
            'OT - Landing Page': 'DEFAULT'
        },
        externalID: 'open_captcha'
    },
    // Main Popup variants (Default)
    MAIN_DISCOUNT: {
        offerTextLine1:  'Lock in',
        offerTextLine2:  'Up to 40% off',
        emailPromptText: 'Enter your email to claim your special offer',
        smsPromptText:   'with texts to claim up to 40% off',
        teaserText:      'Up to 40% Off',
        showQuiz:        true,
        propertyValues:  {
            'OT - Test':    'DISCOUNT',
            'OT - Variant': 'MAIN_DISCOUNT'
        },
        externalID: 'standard'
    }
};
